import {createSelector, createSlice} from '@reduxjs/toolkit';
// Selectors
import {cartEndpoints, selectCartItems} from '~/api/designs/cartApi';

import {saveAddCart} from '~/actions/AnalyticsActions';
import {loadStyles} from '~/actions/CatalogActions';
import {fetch as fetchLoadDesign} from '~/actions/DesignActions';
import {updateStylesCategoryPaths} from '~/actions/ProductActions';

import {getInferredStyleIdFromColorId} from '~/utils/url';

// Constants
import {getStyle, getStyles} from '~/selectors/CatalogSelectors';
import {getCompositeId} from '~/selectors/DesignSelectors';
import {
  getAnchorStyleId,
  getCategoryPathForSelection,
  getSelectionsToArray
} from '~/selectors/ProductSelectors';

export const initialState = {
  recentlyAddedAnchorStyleId: null,
  recentlyAddedItems: []
};

export const addCurrentDesignToCart = () => async (dispatch, getState) => {
  const state = getState();
  const designCompositeId = getCompositeId(state);
  const selections = getSelectionsToArray(state);
  const categoryPath = getCategoryPathForSelection(state);
  const recentlyAddedAnchorStyleId = getAnchorStyleId(state);

  const addDesignToCartSubscription = dispatch(
    cartEndpoints.addDesignToCart.initiate({designCompositeId})
  );
  const {data: updatedCartData, error} = await addDesignToCartSubscription;

  if (error) throw error;

  const recentlyAddedItems = Object.values(updatedCartData.items)
    .filter(item => item.designCompositeId === designCompositeId)
    .map(item => {
      const selectionRecord = selections.find(
        selection => parseInt(selection.get('colorId'), 10) === item.colorId
      );
      return {
        ...item,
        styleId: selectionRecord?.get('styleId'),
        categoryPath,
        visible: selectionRecord?.visible
      };
    });

  dispatch(setCartRecentlyAddedItems(recentlyAddedItems));
  dispatch(saveAddCart({recentlyAddedItems}));

  if (recentlyAddedItems.length) {
    dispatch(setCartRecentlyAddedAnchorStyleId(recentlyAddedAnchorStyleId));
  }

  addDesignToCartSubscription?.unsubscribe();
};

export const loadDesignAddToCart =
  ({designCompositeId, useCustomSelection, customSelection}) =>
  async dispatch => {
    await dispatch(
      fetchLoadDesign({cid: designCompositeId, autoSelectProduct: false})
    );
    await dispatch(
      addCurrentDesignToCart({useCustomSelection, customSelection})
    );
  };

export const updateCartItemsCategoryPaths =
  () => async (dispatch, getState) => {
    const state = getState();
    const cartItems = selectCartItems(state);
    const missingStyles = [];
    const cartItemsStyles = cartItems
      ?.map(item => {
        const styleId =
          item?.styleId || getInferredStyleIdFromColorId(item?.colorId);
        const style = getStyle(state, {styleId});

        if (!style) missingStyles.push(styleId);

        return styleId;
      })
      .filter(item => {
        return item !== null && item !== undefined ? item : null;
      });

    await dispatch(loadStyles(missingStyles));
    dispatch(updateStylesCategoryPaths(cartItemsStyles));
  };

// Cart slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartRecentlyAddedItems: (state, action) => {
      state.recentlyAddedItems = action.payload;
    },
    setCartRecentlyAddedAnchorStyleId: (state, action) => {
      state.recentlyAddedAnchorStyleId = action.payload;
    }
  }
});

export const {setCartRecentlyAddedItems, setCartRecentlyAddedAnchorStyleId} =
  cartSlice.actions;
export default cartSlice.reducer;

// Cart selectors
export const selectRecentlyAddedCartItems = state =>
  state.cart.recentlyAddedItems;

export const selectRecentlyAddedItemsSubTotal = createSelector(
  [selectRecentlyAddedCartItems],
  recentlyAddedItems => {
    return recentlyAddedItems?.reduce((acc, item) => acc + item?.subTotal, 0);
  }
);

export const selectCartRecentlyAddedAnchorStyleId = state =>
  state.cart.recentlyAddedAnchorStyleId;

export const selectCartRecentlyAddedAnchorStyleType = createSelector(
  [selectCartRecentlyAddedAnchorStyleId, getStyles],
  (anchorStyleId, styles) => {
    const style = styles.find(style => style.id === anchorStyleId);
    return style ? style.style_type : null;
  }
);

export const selectIsCurrentDesignInCart = createSelector(
  [getCompositeId, selectCartItems],
  (compositeId, cartItems) => {
    if (compositeId === null) {
      return false;
    } else {
      return cartItems.some(
        cartItem => cartItem.designCompositeId === compositeId
      );
    }
  }
);

export const selectAlreadyAddedCartItems = createSelector(
  [selectRecentlyAddedCartItems, selectCartItems],
  (recentlyAddedItems, cartItems) => {
    const recentlyAddedItemsIds = recentlyAddedItems.map(item => item.id);
    return cartItems.filter(
      cartItem => !recentlyAddedItemsIds.includes(cartItem.id)
    );
  }
);

export const selectDesignCidsInCart = createSelector(
  [selectCartItems],
  cartItems => {
    return [...new Set(cartItems.map(cartItem => cartItem.designCompositeId))];
  }
);

export const selectDesignNamesInCart = createSelector(
  [selectCartItems],
  cartItems => {
    return [...new Set(cartItems.map(cartItem => cartItem.designName))];
  }
);

export const selectPreviousProductsDataFromCart = createSelector(
  [selectRecentlyAddedCartItems],
  recentlyAddedCartItems =>
    recentlyAddedCartItems.map(cartItem => {
      return {
        colorId: cartItem.colorId.toString(),
        styleId: cartItem.styleId,
        categoryPath: cartItem.categoryPath,
        quoteQuantities: cartItem.quoteQuantities
      };
    })
);

export const selectVisibleRecentlyAddedCartItems = createSelector(
  [selectRecentlyAddedCartItems],
  recentlyAddedCartItems => {
    return recentlyAddedCartItems.filter(
      cartItem => cartItem.visible !== false
    );
  }
);

const selectMergedCartItems = createSelector(
  [selectRecentlyAddedCartItems],
  recentlyAddedCartItems => {
    return recentlyAddedCartItems.filter(
      cartItem => cartItem.visible === false
    );
  }
);

export const selectAllPreviousItemsInCart = createSelector(
  [selectAlreadyAddedCartItems, selectMergedCartItems],
  (alreadyAddedCartItems, mergedCartItems) => {
    return alreadyAddedCartItems.concat(mergedCartItems);
  }
);
